/**
 * Edge 神经语音能选的音色（2026-10-30）。和服务器那一份（apps/api 的 edge-tts.ts 的 EDGE_VOICES）必须一样 ——
 * 有测试对照（edge-voices.test.ts）。「声音与语音」卡上「音色」那个下拉就是它（清单里的 optionsRef 'VOICES'）。
 */
export const EDGE_VOICE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['zh-CN-YunyangNeural', '云扬 · 男 · 专业播音腔（推荐：纪律播报）'],
  ['zh-CN-YunjianNeural', '云健 · 男 · 激情有力（熔断、警报）'],
  ['zh-CN-XiaoxiaoNeural', '晓晓 · 女 · 温暖亲和（日常提醒）'],
  ['zh-CN-YunxiNeural', '云希 · 男 · 阳光活力'],
  ['zh-CN-XiaoyiNeural', '晓伊 · 女 · 活泼灵动'],
  ['zh-CN-YunxiaNeural', '云夏 · 男 · 少年感'],
  ['zh-CN-liaoning-XiaobeiNeural', '晓北 · 女 · 东北话'],
  ['zh-CN-shaanxi-XiaoniNeural', '晓妮 · 女 · 陕西话'],
  ['zh-HK-HiuMaanNeural', '曉曼 · 女 · 粵語'],
  ['zh-HK-WanLungNeural', '雲龍 · 男 · 粵語'],
  ['zh-TW-HsiaoChenNeural', '曉臻 · 女 · 台灣國語'],
  ['en-US-AriaNeural', 'Aria · female · English'],
  ['en-US-GuyNeural', 'Guy · male · English']
];

const IDS: ReadonlySet<string> = new Set(EDGE_VOICE_OPTIONS.map(([id]) => id));

/** 界面语言跟着挑音色（voiceFollow 开着时）。和服务器 apps/api 的 edge-tts.ts 的 pickEdgeVoice 一样（有测试对照） */
export function pickEdgeVoice(chosen: unknown, lang: string, follow: boolean): string {
  const own = typeof chosen === 'string' && IDS.has(chosen) ? chosen : 'zh-CN-YunyangNeural';
  if (!follow) return own;
  if (lang === 'en') return own.startsWith('en-') ? own : 'en-US-AriaNeural';
  if (lang === 'zh-HK') return own.startsWith('zh-HK') || own.startsWith('zh-TW') ? own : 'zh-HK-HiuMaanNeural';
  return own.startsWith('zh-CN') ? own : 'zh-CN-YunyangNeural';
}

/** 「每天随机」用的：同一天算出同一个数。服务器那边是同一个算法 */
export function dayIndex(day: string, n: number): number {
  let h = 0;
  for (const ch of day) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % n;
}

/**
 * 这一次用哪个音色（音色模式：固定 / 每次随机 / 每天随机）。和服务器的 chooseVoice 一样（有测试对照）——
 * 网页自己先挑好，才能按「这句话 + 这个音色」去本机缓存里找，找到了就不用问服务器
 */
export function chooseEdgeVoice(flat: Readonly<Record<string, unknown>>, lang: string, now = new Date(), rnd: () => number = Math.random): string {
  const follow = flat['voiceFollow'] !== false;
  if (follow && lang !== 'zh-CN') return pickEdgeVoice(flat['ge_vEdgeName'], lang, true);
  const pool = EDGE_VOICE_OPTIONS.filter(([id]) => id.startsWith('zh-CN')).map(([id]) => id);
  if (flat['ge_vMode'] === 'random') return pool[Math.floor(rnd() * pool.length) % pool.length]!;
  if (flat['ge_vMode'] === 'daily') {
    /* 按北京时间的日期：同一天固定一个 */
    const day = new Date(now.getTime() + 8 * 3600_000).toISOString().slice(0, 10);
    return pool[dayIndex(day, pool.length)]!;
  }
  return pickEdgeVoice(flat['ge_vEdgeName'], lang, false);
}

/**
 * Edge 浏览器自带的神经语音里，哪一个是我们这个音色（2026-10-31）。
 * Edge 的名字形如「Microsoft Xiaoxiao Online (Natural) - Chinese (Mainland)」，和服务器合成的是同一套微软神经语音，
 * 在用户自己的浏览器里念，不经过我们的服务器
 */
export function isEdgeBuiltinFor(name: string, voiceLang: string, id: string): boolean {
  const m = /^([a-z]{2,3}-[A-Z]{2})(?:-[a-z]+)?-(.+)Neural$/.exec(id);
  if (!m) return false;
  return name.startsWith(`Microsoft ${m[2]} Online`) && voiceLang.replace('_', '-').startsWith(m[1]!);
}
